import type { ReactNode } from "react";
import { useTranslate } from "@refinedev/core";
import { Button, FormCard } from "@fawkes/ui";

// jmix-exit E3a.3: the shared error / empty / loading + placeholder conventions every Epic 7–9
// screen reuses. They compose @fawkes/ui (FormCard, Button) on the --fawkes-* tokens and add only
// screen-scoped `.fawkes-state-*` CSS (Golden Rule: additive, never an edit to a shared component).
// A migrated list/detail screen renders: <LoadingState/> while fetching, <ErrorState/> on failure,
// <EmptyState/> when the result set is empty, and the real content otherwise.

function StateCard({ children }: { children: ReactNode }) {
  return (
    <div className="fawkes-state">
      <FormCard>{children}</FormCard>
    </div>
  );
}

/** Loading — a token-styled busy card. `aria-busy` so assistive tech announces the wait. */
export function LoadingState({ label }: { label?: string }) {
  const t = useTranslate();
  return (
    <div className="fawkes-state" aria-busy="true">
      <FormCard>
        <div className="fawkes-state__body">
          <span className="fawkes-state__spinner" aria-hidden="true" />
          <p className="fawkes-state__text">{label ?? t("state.loading")}</p>
        </div>
      </FormCard>
    </div>
  );
}

/** Empty — a non-error "no data yet" state, distinct from a failure. */
export function EmptyState({ title, body }: { title?: string; body?: string }) {
  const t = useTranslate();
  return (
    <StateCard>
      <div className="fawkes-state__body">
        <h2 className="fawkes-state__title">{title ?? t("state.empty.title")}</h2>
        <p className="fawkes-state__text">{body ?? t("state.empty.body")}</p>
      </div>
    </StateCard>
  );
}

/** Error — a failure state with an optional retry action. */
export function ErrorState({
  title,
  body,
  onRetry,
}: {
  title?: string;
  body?: string;
  onRetry?: () => void;
}) {
  const t = useTranslate();
  return (
    <StateCard>
      <div className="fawkes-state__body" role="alert">
        <h2 className="fawkes-state__title">{title ?? t("state.error.title")}</h2>
        <p className="fawkes-state__text">{body ?? t("state.error.body")}</p>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            {t("state.error.retry")}
          </Button>
        )}
      </div>
    </StateCard>
  );
}

/**
 * Placeholder — the in-SPA "this screen is built in a later phase" surface. In a big-bang migration
 * there is no legacy Jmix screen to hand off to, so every not-yet-built nav target lands here with
 * the screen's name, keeping the operator inside the authed shell.
 */
export function Placeholder({ title, onHome }: { title: string; onHome?: () => void }) {
  const t = useTranslate();
  return (
    <StateCard>
      <div className="fawkes-state__body">
        <h2 className="fawkes-state__title">{title}</h2>
        <p className="fawkes-state__text">{t("shell.underConstruction.body")}</p>
        <p className="fawkes-state__note">{t("state.placeholder.note")}</p>
        {onHome && (
          <Button variant="secondary" onClick={onHome}>
            {t("shell.underConstruction.home")}
          </Button>
        )}
      </div>
    </StateCard>
  );
}
